import type { ChecklistItem } from '../types/schema'
import type { TimelineElement } from '../types/timelineElement'
import { getChecklistBlockState, type ChecklistBlockReason } from './checklistDependencyState'
import type { RepeatPagesInput } from '../services/repeat/types'

export type ReadinessEntityType = 'ELEMENT' | 'CHECKLIST'

export type ReadinessResult = {
  isReady: boolean
  isComplete: boolean
  blockedReasonCodes: string[]
  blockReasons: ChecklistBlockReason[]
}

export const getChecklistReadiness = (
  checklist: ChecklistItem,
  checklistById: ReadonlyMap<string, ChecklistItem>,
  periodKey?: string,
  pages?: RepeatPagesInput,
  timeZone?: string,
): ReadinessResult => {
  const blockState = getChecklistBlockState(checklist, checklistById, periodKey, pages, timeZone)
  const isComplete = checklist.isCompleted === true || checklist.workflowStatus === 'COMPLETED' || checklist.workflowStatus === 'NOT_REQUIRED' || checklist.boardStatus === 'COMPLETE'
  return {
    isReady: isComplete && !blockState.isBlocked,
    isComplete,
    blockedReasonCodes: blockState.reasons.map((reason) => reason.code),
    blockReasons: blockState.reasons,
  }
}

export const getElementReadiness = (element: TimelineElement): ReadinessResult => {
  const isComplete = element.boardStatus === 'COMPLETE' || Number(element.progress ?? 0) >= 100
  return {
    isReady: isComplete,
    isComplete,
    blockedReasonCodes: [],
    blockReasons: [],
  }
}

export const getAgendaNodeReadiness = (
  nodeType: ReadinessEntityType,
  nodeId: string,
  input: {
    elements: readonly TimelineElement[]
    checklistItems: readonly ChecklistItem[]
    periodKey?: string
    pages?: RepeatPagesInput
    timeZone?: string
  },
): ReadinessResult => {
  if (nodeType === 'ELEMENT') {
    const element = input.elements.find((candidate) => candidate.id === nodeId)
    return element ? getElementReadiness(element) : { isReady: false, isComplete: false, blockedReasonCodes: ['DEPENDENCY_MISSING'], blockReasons: [{ code: 'DEPENDENCY_MISSING', targetId: nodeId }] }
  }
  const checklistById = new Map(input.checklistItems.map((item) => [item.id, item]))
  const checklist = checklistById.get(nodeId)
  return checklist ? getChecklistReadiness(checklist, checklistById, input.periodKey, input.pages, input.timeZone) : { isReady: false, isComplete: false, blockedReasonCodes: ['DEPENDENCY_MISSING'], blockReasons: [{ code: 'DEPENDENCY_MISSING', targetId: nodeId }] }
}
